import React, { useEffect, useState } from "react";
import axios from "axios";
import { FiLock } from "react-icons/fi";

/** Live edit-lock state for a content item from this admin's point of view. */
export type EditLockState = {
	/** Who currently holds the lock (this admin or someone else), or null if free. */
	lockedBy: string | null;
	/** True when this admin holds the lock. */
	isMine: boolean;
};

const HEARTBEAT_MS = 30_000;

/**
 * Advisory edit lock for a content id. On open it acquires the lock and then heartbeats (each call
 * extends the server TTL) so the lock stays alive while the editor is open and lapses automatically
 * once it closes. Releases on unmount. If another admin already holds it, `isMine` is false and
 * `lockedBy` names them so the editor can warn. Entirely best-effort: a lock failure never blocks
 * editing (state simply stays "free"). Pass a falsy id (e.g. a brand-new unsaved page) to disable.
 */
export const useEditLock = (contentId?: string | null): EditLockState => {
	const [state, setState] = useState<EditLockState>({ lockedBy: null, isMine: false });

	useEffect(() => {
		const id = (contentId ?? "").trim();
		if (!id) {
			setState({ lockedBy: null, isMine: false });
			return;
		}

		let cancelled = false;
		const acquire = async () => {
			try {
				const res = await axios.post(`/api/locks/${encodeURIComponent(id)}/acquire`);
				if (cancelled) return;
				setState({ lockedBy: res.data?.lockedBy ?? null, isMine: Boolean(res.data?.isMine) });
			} catch {
				// Lock store unreachable → behave as if free; never block editing.
				if (!cancelled) setState({ lockedBy: null, isMine: false });
			}
		};

		acquire();
		const timer = window.setInterval(acquire, HEARTBEAT_MS);

		return () => {
			cancelled = true;
			window.clearInterval(timer);
			// Best-effort release so the next editor can take over immediately (else it lapses on TTL).
			axios.delete(`/api/locks/${encodeURIComponent(id)}`).catch(() => {});
		};
	}, [contentId]);

	return state;
};

/**
 * Non-blocking contention banner: shows only when another admin holds the edit lock. It warns rather
 * than locks the form : saves still work : so the operator decides, matching the soft-lock model.
 */
export const EditLockBanner: React.FC<{ contentId?: string | null; className?: string }> = ({ contentId, className }) => {
	const { lockedBy, isMine } = useEditLock(contentId);
	if (!lockedBy || isMine) return null;
	return (
		<div
			className={`flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-800 ${className ?? ""}`}
			role="alert"
		>
			<FiLock className="h-4 w-4 shrink-0" />
			<span>
				En cours d'édition par <strong>{lockedBy}</strong>. Vos modifications pourraient entrer en conflit.
			</span>
		</div>
	);
};
